import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');
    setFormStatus('submitting');
    setFormMessage('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d9q8haue3oq4jqumti50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '吃瓜51',
    url: 'https://chigua51.com',
    description: '吃瓜51是专注于热门娱乐爆料、明星八卦、社会热点的吃瓜聚合平台',
    foundingDate: '2025',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@chigua51.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '吃瓜51是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜51是专注于热门娱乐爆料、明星八卦、社会热点的吃瓜聚合平台。我们第一时间汇聚最新瓜料、独家内幕，让你随时随地吃瓜不断线，掌握最新热点动态，做最快的吃瓜群众。',
        },
      },
      {
        '@type': 'Question',
        name: '吃瓜51的内容是否真实可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜51的内容来自多渠道信息汇总，我们致力于为读者提供真实、及时的资讯。对于独家爆料内容，我们会标注来源信息，但建议读者理性看待，自行判断内容的真实性。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在吃瓜51上投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的"联系我们"表单提交爆料线索，或者直接发送邮件至 tips@chigua51.com。我们会对每一条线索认真核实，确认真实性后进行报道。',
        },
      },
      {
        '@type': 'Question',
        name: '吃瓜51的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜51保持24小时不间断更新，平均每日更新超过50条爆料内容，确保您随时随地都能吃到最新鲜的瓜。',
        },
      },
      {
        '@type': 'Question',
        name: '如何申请与吃瓜51进行商务合作？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '商务合作请发送邮件至 bd@chigua51.com，我们的商务团队会在1-2个工作日内与您联系，洽谈合作细节。',
        },
      },
    ],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于吃瓜51 - 最新热门吃瓜爆料聚合平台',
    description: '了解吃瓜51的品牌故事、用户口碑和联系方式',
    url: 'https://chigua51.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                关于吃瓜51
              </h1>
            </div>
          </div>

          <section className="py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                    <i className="ri-information-line"></i> 品牌介绍
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                    做全网最快的<span className="text-primary-500">吃瓜</span>群众
                  </h2>
                  <div className="space-y-4 text-foreground-700 leading-relaxed">
                    <p>
                      <strong>吃瓜51</strong>成立于2025年，是国内领先的娱乐爆料与热点资讯聚合平台。我们致力于为全网用户提供最新、最全、最快的娱乐八卦、明星内幕和社会热点资讯，让每一位用户都能第一时间掌握最新动态。
                    </p>
                    <p>
                      在信息爆炸的时代，吃瓜51通过智能算法与专业编辑团队的双重把关，从海量信息中筛选出最具价值的爆料内容，为用户提供高效、精准的资讯服务。我们的使命是：<strong>让吃瓜变得简单而有趣。</strong>
                    </p>
                    <p>
                      目前，吃瓜51日均活跃用户已突破百万，覆盖娱乐八卦、明星内幕、社会热点、网络热议、综艺剧透等八大内容板块，日均内容更新量超过50条，已成为国内最具影响力的吃瓜平台之一。
                    </p>
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20media%20newsroom%20with%20large%20screens%20displaying%20entertainment%20news%2C%20warm%20ambient%20lighting%2C%20trendy%20office%20design%2C%20editorial%20atmosphere%2C%20creative%20workspace%20with%20red%20and%20orange%20accents%2C%20professional%20yet%20playful%20vibe%2C%20no%20people&width=800&height=600&seq=about-brand-chigua51&orientation=landscape"
                    alt="吃瓜51品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-4">
                  <i className="ri-star-line"></i> 用户口碑
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  百万用户的<span className="text-accent-500">共同选择</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {[
                  { value: '100万+', label: '日均活跃用户', icon: 'ri-user-smile-line' },
                  { value: '50条+', label: '日均更新爆料', icon: 'ri-article-line' },
                  { value: '99.7%', label: '用户满意度', icon: 'ri-heart-line' },
                  { value: '8大', label: '内容分类板块', icon: 'ri-stack-line' },
                ].map((stat, index) => (
                  <div
                    key={index}
                    className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm font-medium mb-4">
                  <i className="ri-question-answer-line"></i> 常见问题
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  你可能想<span className="text-secondary-500">了解</span>
                </h2>
              </div>
              <div className="space-y-3">
                {[
                  { q: '吃瓜51是什么平台？', a: '吃瓜51是专注于热门娱乐爆料、明星八卦、社会热点的吃瓜聚合平台。我们第一时间汇聚最新瓜料、独家内幕，让你随时随地吃瓜不断线，掌握最新热点动态，做最快的吃瓜群众。' },
                  { q: '吃瓜51的内容是否真实可靠？', a: '吃瓜51的内容来自多渠道信息汇总，我们致力于为读者提供真实、及时的资讯。对于独家爆料内容，我们会标注来源信息，但建议读者理性看待，自行判断内容的真实性。' },
                  { q: '如何在吃瓜51上投稿爆料？', a: '您可以通过本页面的"联系我们"表单提交爆料线索，或者直接发送邮件至 tips@chigua51.com。我们会对每一条线索认真核实，确认真实性后进行报道。' },
                  { q: '吃瓜51的内容更新频率如何？', a: '吃瓜51保持24小时不间断更新，平均每日更新超过50条爆料内容，确保您随时随地都能吃到最新鲜的瓜。' },
                  { q: '如何申请与吃瓜51进行商务合作？', a: '商务合作请发送邮件至 bd@chigua51.com，我们的商务团队会在1-2个工作日内与您联系，洽谈合作细节。' },
                ].map((faq, index) => (
                  <details key={index} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                      <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.q}</span>
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-500 group-open:text-white transition-all flex-shrink-0">
                        <i className="ri-add-line text-sm group-open:hidden"></i>
                        <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                  <i className="ri-mail-send-line"></i> 联系我们
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  有料要爆？<span className="text-primary-500">联系我们</span>
                </h2>
                <p className="mt-2 text-foreground-500 text-sm">欢迎提供爆料线索、商务合作或任何建议</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-mail-line', title: '爆料邮箱', content: 'tips@chigua51.com' },
                  { icon: 'ri-briefcase-line', title: '商务合作', content: 'bd@chigua51.com' },
                  { icon: 'ri-customer-service-line', title: '客服邮箱', content: 'contact@chigua51.com' },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</h4>
                    <p className="text-sm text-foreground-500">{item.content}</p>
                  </div>
                ))}
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all appearance-none cursor-pointer"
                  >
                    <option value="">请选择主题</option>
                    <option value="爆料投稿">爆料投稿</option>
                    <option value="商务合作">商务合作</option>
                    <option value="意见建议">意见建议</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字符</p>
                </div>

                <div className="honeypot-field">
                  <label htmlFor="phone_alt">备用电话</label>
                  <input
                    type="text"
                    id="phone_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> 提交中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line"></i> 提交留言
                    </span>
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="mt-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-3 rounded-lg bg-primary-100 text-primary-700 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {formMessage}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}